// CurrencyMark — the glyph drawn beside a figure, from the currency's
// RESOLVED presentation.
//
// Three kinds, in the order resolve.ts prefers them (SM-443 types.ts):
//
//   image    — the artwork an operator uploaded on /currencies. This is the
//              one presentation field that can 404 long after it was
//              configured, so a load failure degrades to the monogram here
//              rather than leaving a broken-image box inside a balance. The
//              contract documented that degradation from the start; this is
//              where it finally happens.
//   brand    — the app's brand glyph (⚡). No longer any currency's seeded
//              default: pinning it to GOLD was what made the legacy mark
//              unchangeable. An operator who wants it can still choose it.
//   monogram — a generated badge: the currency's symbol on its own colour.
//              Deterministic, so an unconfigured currency looks the same in
//              every app and every test run.
//
// Colour is never the only signal. The monogram carries the symbol as text,
// and every figure using this mark also exposes the currency's NAME in its
// accessible label — RED and GREEN differ chiefly by hue, and a colour-blind
// or monochrome reader must still be able to tell one balance from the other.

import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/cn";
import { Spark } from "@/components/ui/Spark";
import { useCurrencySettings } from "@/components/currency/CurrencyPresentationProvider";
import { monogramGlyph, resolveCurrencyPresentation, type CurrencyPresentation } from "@/currency";

export interface CurrencyMarkProps {
  presentation: CurrencyPresentation;
  /** Edge length in px. Defaults to ~0.92em so the mark tracks the type size. */
  size?: number;
  className?: string;
  /**
   * Called when configured artwork fails to load and this mark degrades to the
   * monogram.
   *
   * Degrading SILENTLY is right almost everywhere: a balance is about the
   * money, and a 404 on a logo must not turn into an error beside it. It is
   * wrong on exactly one screen — Treasury → Currencies, which is ABOUT the
   * artwork, and where an operator who uploaded a mark needs to be told that
   * what they configured is not what anyone is seeing. That page passes this;
   * nothing else should.
   */
  onImageError?: () => void;
  /**
   * SM-984: a GOLD mark drawing a BROKER's image (the broker page) falls
   * back to the platform's GOLD — the registry image, then the bundled coin
   * — before the monogram, as the website does for that broker's users.
   * Off by default: every other mark degrades exactly as before.
   */
  fallbackToPlatformGold?: boolean;
}

export function CurrencyMark({
  presentation,
  size,
  className,
  onImageError,
  fallbackToPlatformGold = false,
}: CurrencyMarkProps) {
  const { icon, color, symbol, displayName } = presentation;
  const platform = useCurrencySettings();
  // The images to try, in order: the configured one, then (opt-in, GOLD
  // only) the registry's GOLD image and the bundled coin.
  const sources = useMemo(() => {
    if (icon.kind !== "image") return [];
    const out = [icon.src];
    if (!fallbackToPlatformGold || presentation.code !== "GOLD") return out;
    for (const settings of [platform, undefined]) {
      const gold = resolveCurrencyPresentation("GOLD", settings).icon;
      if (gold.kind === "image" && !out.includes(gold.src)) out.push(gold.src);
    }
    return out;
  }, [icon, presentation.code, fallbackToPlatformGold, platform]);
  const [failed, setFailed] = useState(0);

  // A new src deserves a fresh attempt: an operator who fixes a broken URL
  // should see the new artwork without a reload.
  const sourcesKey = sources.join("\n");
  useEffect(() => setFailed(0), [sourcesKey]);
  const src = sources[failed];

  const boxStyle = size != null ? { width: size, height: size } : { width: "0.92em", height: "0.92em" };

  if (icon.kind === "image" && src) {
    const attempt = failed;
    return (
      <img
        key={src}
        src={src}
        alt=""
        aria-hidden
        onError={() => {
          setFailed((n) => (n === attempt ? attempt + 1 : n));
          // the configured artwork failed (a fallback failing is not news)
          if (attempt === 0) onImageError?.();
        }}
        className={cn("inline-block shrink-0 rounded-sm object-contain", className)}
        style={boxStyle}
      />
    );
  }

  if (icon.kind === "brand") {
    return <Spark className={cn("shrink-0 opacity-90", className)} style={boxStyle} />;
  }

  return (
    <span
      aria-hidden
      title={displayName}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-sm px-0.5 text-[0.6em] font-bold leading-none",
        className,
      )}
      style={{ color, backgroundColor: `${color}22`, minWidth: "1.5em" }}
    >
      {monogramGlyph(symbol)}
    </span>
  );
}

/**
 * Where the mark actually being drawn came from, given what the registry row
 * configured.
 *
 * Lives HERE because this is the one file allowed to look inside a resolved
 * icon (the parity suite asserts that, so every mark degrades identically).
 * The settings page needs the answer in words — "the coin you are looking at
 * is the seeded one, not the file you uploaded" — and getting it by reading
 * `icon` at the call site is how a second, divergent opinion about artwork
 * gets written.
 *
 *   uploaded  — the configured URL is what will be drawn.
 *   rejected  — a URL IS configured, and resolve.ts refused it (not http(s),
 *               root-relative or a data: image). The seed is drawn instead,
 *               and the operator's configuration is having no effect at all.
 *   generated — the monogram: a letter badge, never a currency's default.
 *   seeded    — nothing configured; the platform's own artwork.
 */
export type MarkSource = "uploaded" | "rejected" | "generated" | "seeded";

export function markSource(
  presentation: CurrencyPresentation,
  configuredImageURL?: string,
): MarkSource {
  const configured = (configuredImageURL ?? "").trim();
  const { icon } = presentation;
  if (icon.kind === "image" && configured !== "" && icon.src === configured) return "uploaded";
  if (configured !== "") return "rejected";
  return icon.kind === "monogram" ? "generated" : "seeded";
}
